// Zettl App - Financial Dashboard
import { Fragment, Suspense, type ReactNode } from "react";
import { lazyWithReload } from "@/lib/chunkReload";
import * as Sentry from "@sentry/react";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { AnalyticsPageviews } from "@/components/consent/AnalyticsPageviews";
import { CookieConsent } from "@/components/consent/CookieConsent";
import { ErrorFallback } from "@/components/ErrorFallback";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/hooks/useLanguage";
import { ModeProvider } from "@/hooks/useMode";
import { OrganizationProvider } from "@/hooks/useOrganization";
import { useIsDemoMode } from "@/hooks/useDemoMode";
import {
  CZECH_ROOT_PATHS,
  GUIDE_PAGE_KEYS,
  PUBLIC_PAGE_KEYS,
  PUBLIC_SEGMENTS,
  type PublicPageKey,
} from "@/lib/site/publicPages";
import { LanguageSync } from "@/components/LanguageSync";
import { ScrollToTop } from "@/components/ScrollToTop";
import { LanguageRoute } from "@/components/LanguageRoute";
import { PreferencesSync } from "@/components/PreferencesSync";
import { SeoHead } from "@/components/SeoHead";
// Public pages are prerendered (scripts/prerender.mjs) and stay in the entry
// chunk so they never flash; everything behind sign-in loads on demand.
import Landing from "./pages/Landing";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import Dpa from "./pages/Dpa";
import Terms from "./pages/Terms";
import Cookies from "./pages/Cookies";
import Contact from "./pages/Contact";
import Changelog from "./pages/Changelog";
import Guide from "./pages/Guide";
import Guides from "./pages/Guides";
import Tools from "./pages/Tools";
import { AdminRoute } from "./components/admin/AdminRoute";
import { PlanGateHost } from "@/components/billing/PlanGate";
import { RequireFeature } from "@/components/billing/RequireFeature";

/** The public pages, for the Czech root routes (the `/:lang/…` routes are below). */
const PUBLIC_PAGE_ELEMENTS: Record<PublicPageKey, JSX.Element> = {
  landing: <Landing />,
  contact: <Contact />,
  terms: <Terms />,
  privacy: <Privacy />,
  dpa: <Dpa />,
  cookies: <Cookies />,
  changelog: <Changelog />,
  guides: <Guides />,
  tools: <Tools />,
  guideAccountants: <Guide pageKey="guideAccountants" />,
  guidePohoda: <Guide pageKey="guidePohoda" />,
  guidePohodaHowTo: <Guide pageKey="guidePohodaHowTo" />,
  guideMatching: <Guide pageKey="guideMatching" />,
  guideFioApi: <Guide pageKey="guideFioApi" />,
  guideGpcImport: <Guide pageKey="guideGpcImport" />,
  toolGpcToCsv: <Guide pageKey="toolGpcToCsv" />,
  toolIsdocViewer: <Guide pageKey="toolIsdocViewer" />,
  toolQrPayment: <Guide pageKey="toolQrPayment" />,
  toolAccountIban: <Guide pageKey="toolAccountIban" />,
  toolGpcPohoda: <Guide pageKey="toolGpcPohoda" />,
  guideFioToken: <Guide pageKey="guideFioToken" />,
  guideMonthClose: <Guide pageKey="guideMonthClose" />,
  guideEmailInbox: <Guide pageKey="guideEmailInbox" />,
  guideAccountant: <Guide pageKey="guideAccountant" />,
  guideIsdoc: <Guide pageKey="guideIsdoc" />,
  guideForeignCurrency: <Guide pageKey="guideForeignCurrency" />,
  guideSplitPayment: <Guide pageKey="guideSplitPayment" />,
  guideMcp: <Guide pageKey="guideMcp" />,
  guideVsSpreadsheet: <Guide pageKey="guideVsSpreadsheet" />,
  guideFakturoid: <Guide pageKey="guideFakturoid" />,
  guideIdoklad: <Guide pageKey="guideIdoklad" />,
};
const Auth = lazyWithReload(() => import("./pages/Auth"));
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword"));
const Unsubscribe = lazyWithReload(() => import("./pages/Unsubscribe"));
const Dashboard = lazyWithReload(() => import("./pages/Dashboard"));
const MissingDocuments = lazyWithReload(() => import("./pages/MissingDocuments"));
const AccountantClients = lazyWithReload(() => import("./pages/AccountantClients"));
const DesignLab = lazyWithReload(() => import("./pages/DesignLab"));
const OAuthAuthorize = lazyWithReload(() => import("./pages/OAuthAuthorize"));
const Analytics = lazyWithReload(() => import("./pages/Analytics"));
const AccountPageContent = lazyWithReload(() =>
  import("./pages/settings/AccountPage").then((m) => ({ default: m.AccountPageContent }))
);
const OrganizationPageContent = lazyWithReload(() =>
  import("./pages/settings/OrganizationPage").then((m) => ({ default: m.OrganizationPageContent }))
);
const InviteAccept = lazyWithReload(() => import("./pages/InviteAccept"));
const JoinOrganization = lazyWithReload(() => import("./pages/JoinOrganization"));
const SettingsLayout = lazyWithReload(() =>
  import("./pages/settings/SettingsLayout").then((m) => ({ default: m.SettingsLayout }))
);
const BillingPageContent = lazyWithReload(() =>
  import("./pages/settings/BillingPage").then((m) => ({ default: m.BillingPageContent }))
);
const IntegrationsPageContent = lazyWithReload(() =>
  import("./pages/settings/IntegrationsPage").then((m) => ({ default: m.IntegrationsPageContent }))
);
const AccountingPageContent = lazyWithReload(() =>
  import("./pages/settings/AccountingPage").then((m) => ({ default: m.AccountingPageContent }))
);
const PreferencesPageContent = lazyWithReload(() =>
  import("./pages/settings/PreferencesPageContent").then((m) => ({
    default: m.PreferencesPageContent,
  }))
);
const Clients = lazyWithReload(() => import("./pages/Clients"));
const ClientDetail = lazyWithReload(() => import("./pages/ClientDetail"));
const Invoices = lazyWithReload(() => import("./pages/Invoices"));
const Receipts = lazyWithReload(() => import("./pages/Receipts"));
const Capture = lazyWithReload(() => import("./pages/Capture"));
const Transactions = lazyWithReload(() => import("./pages/Transactions"));
const TransactionImportExport = lazyWithReload(() => import("./pages/TransactionImportExport"));
const InvoiceCreate = lazyWithReload(() => import("./pages/InvoiceCreate"));
const InvoiceEdit = lazyWithReload(() => import("./pages/InvoiceEdit"));
const InvoiceDetail = lazyWithReload(() => import("./pages/InvoiceDetail"));
const Webfaktura = lazyWithReload(() => import("./pages/Webfaktura"));
const AuditLogs = lazyWithReload(() => import("./pages/AuditLogs"));
const DataExport = lazyWithReload(() => import("./pages/DataExport"));
const Templates = lazyWithReload(() => import("./pages/Templates"));
const TemplateCreate = lazyWithReload(() => import("./pages/TemplateCreate"));
const TemplateEdit = lazyWithReload(() => import("./pages/TemplateEdit"));
const PersonalOverview = lazyWithReload(() => import("./pages/PersonalOverview"));
const PersonalAnalytics = lazyWithReload(() => import("./pages/PersonalAnalytics"));
const Budget = lazyWithReload(() => import("./pages/Budget"));
const BudgetTracker = lazyWithReload(() => import("./pages/BudgetTracker"));
const BudgetTrackerAnalytics = lazyWithReload(() => import("./pages/BudgetTrackerAnalytics"));
const BusinessBudgetTracker = lazyWithReload(() => import("./pages/BusinessBudgetTracker"));
const IconDownloads = lazyWithReload(() => import("./pages/IconDownloads"));
const Feedback = lazyWithReload(() => import("./pages/Feedback"));
const AdminOverview = lazyWithReload(() => import("./pages/admin/AdminOverview"));
const AdminCompanies = lazyWithReload(() => import("./pages/admin/AdminCompanies"));
const AdminCompanyDetail = lazyWithReload(() => import("./pages/admin/AdminCompanyDetail"));
const AdminUsers = lazyWithReload(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazyWithReload(() => import("./pages/admin/AdminUserDetail"));
const AdminInbox = lazyWithReload(() => import("./pages/admin/AdminInbox"));
const AdminBilling = lazyWithReload(() => import("./pages/admin/AdminBilling"));
const AdminDev = lazyWithReload(() => import("./pages/admin/AdminDev"));
const AdminGrowth = lazyWithReload(() => import("./pages/admin/AdminGrowth"));
const BankConnectionsMonitor = lazyWithReload(() => import("./pages/admin/BankConnectionsMonitor"));
const SyncErrorLogs = lazyWithReload(() => import("./pages/admin/SyncErrorLogs"));
const MonitoringIncidents = lazyWithReload(() => import("./pages/admin/MonitoringIncidents"));
const FreeAccess = lazyWithReload(() => import("./pages/admin/FreeAccess"));

function ProfileToSettingsRedirect() {
  const { lang } = useParams();
  return <Navigate to={`/${lang}/settings/account`} replace />;
}

/** A guide's Czech page lives only at its root slug: `/cs/<segment>` goes there. */
function GuideLanguageGate({ czechPath, children }: { czechPath: string; children: JSX.Element }) {
  const { lang } = useParams();
  return lang === "cs" ? <Navigate to={czechPath} replace /> : children;
}

function SettingsIndexRedirect() {
  const { lang } = useParams();
  return <Navigate to={`/${lang}/settings/account`} replace />;
}

/**
 * Remounts every page when sample-data mode switches (src/lib/demo), so each
 * query starts again under its new key and no page keeps the other mode's rows.
 */
function DemoBoundary({ children }: { children: ReactNode }) {
  const demo = useIsDemoMode();
  return <Fragment key={demo ? "demo" : "real"}>{children}</Fragment>;
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        enableSystem={false}
        storageKey="zettl-theme"
      >
        <LanguageProvider>
          <ModeProvider>
            <OrganizationProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <VercelAnalytics />
                <SpeedInsights />
                <BrowserRouter
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <ScrollToTop />
                  <LanguageSync />
                  <PreferencesSync />
                  <SeoHead />
                  <PlanGateHost />
                  <AnalyticsPageviews />
                  <CookieConsent />
                  <DemoBoundary>
                    <Routes>
                      {/* Czech public pages at the root (zettl.cz): `/`, `/kontakt` …
                        zettlapp.com redirects everything public here (vercel.json). */}
                      {PUBLIC_PAGE_KEYS.map((key) => (
                        <Route
                          key={key}
                          path={CZECH_ROOT_PATHS[key]}
                          element={
                            <LanguageRoute lang="cs">{PUBLIC_PAGE_ELEMENTS[key]}</LanguageRoute>
                          }
                        />
                      ))}

                      {/* OAuth consent screen for the MCP cloud connector.
                        Deliberately outside the /:lang prefix: the URL is
                        published in OAuth metadata and must stay stable. */}
                      <Route
                        path="/oauth/authorize"
                        element={
                          <Suspense fallback={null}>
                            <OAuthAuthorize />
                          </Suspense>
                        }
                      />

                      {/* Language-prefixed routes */}
                      <Route
                        path="/:lang"
                        element={
                          <LanguageRoute>
                            <Landing />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/auth"
                        element={
                          <LanguageRoute>
                            <Auth />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/reset-password"
                        element={
                          <LanguageRoute>
                            <ResetPassword />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/unsubscribe"
                        element={
                          <LanguageRoute>
                            <Unsubscribe />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/dashboard"
                        element={
                          <LanguageRoute>
                            <Dashboard />
                          </LanguageRoute>
                        }
                      />
                      {import.meta.env.DEV && (
                        <Route
                          path="/:lang/design-lab"
                          element={
                            <LanguageRoute>
                              <Suspense fallback={null}>
                                <DesignLab />
                              </Suspense>
                            </LanguageRoute>
                          }
                        />
                      )}
                      <Route
                        path="/:lang/analytics"
                        element={
                          <LanguageRoute>
                            <RequireFeature feature="budgetAnalytics">
                              <Analytics />
                            </RequireFeature>
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/profile"
                        element={
                          <LanguageRoute>
                            <ProfileToSettingsRedirect />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/settings"
                        element={
                          <LanguageRoute>
                            <SettingsLayout />
                          </LanguageRoute>
                        }
                      >
                        <Route index element={<SettingsIndexRedirect />} />
                        <Route path="account" element={<AccountPageContent />} />
                        <Route path="organization" element={<OrganizationPageContent />} />
                        {/* Team was merged into the Organization tab */}
                        <Route path="team" element={<Navigate to="../organization" replace />} />
                        <Route path="preferences" element={<PreferencesPageContent />} />
                        <Route path="integrations" element={<IntegrationsPageContent />} />
                        <Route path="accounting" element={<AccountingPageContent />} />
                        <Route path="billing" element={<BillingPageContent />} />
                      </Route>
                      <Route
                        path="/:lang/invite/:token"
                        element={
                          <LanguageRoute>
                            <InviteAccept />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/join/:code"
                        element={
                          <LanguageRoute>
                            <JoinOrganization />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/feedback"
                        element={
                          <LanguageRoute>
                            <Feedback />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/audit-logs"
                        element={
                          <LanguageRoute>
                            <AuditLogs />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/data-export"
                        element={
                          <LanguageRoute>
                            <DataExport />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/privacy"
                        element={
                          <LanguageRoute>
                            <Privacy />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/dpa"
                        element={
                          <LanguageRoute>
                            <Dpa />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/terms"
                        element={
                          <LanguageRoute>
                            <Terms />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/cookies"
                        element={
                          <LanguageRoute>
                            <Cookies />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/contact"
                        element={
                          <LanguageRoute>
                            <Contact />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/changelog"
                        element={
                          <LanguageRoute>
                            <Changelog />
                          </LanguageRoute>
                        }
                      />
                      {/* Guide pages: English at /en/…, Czech only at its root slug. */}
                      {GUIDE_PAGE_KEYS.map((key) => (
                        <Route
                          key={key}
                          path={`/:lang/${PUBLIC_SEGMENTS[key]}`}
                          element={
                            <LanguageRoute>
                              <GuideLanguageGate czechPath={CZECH_ROOT_PATHS[key]}>
                                {PUBLIC_PAGE_ELEMENTS[key]}
                              </GuideLanguageGate>
                            </LanguageRoute>
                          }
                        />
                      ))}
                      <Route
                        path="/:lang/clients"
                        element={
                          <LanguageRoute>
                            <Clients />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/clients/:id"
                        element={
                          <LanguageRoute>
                            <ClientDetail />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/invoices"
                        element={
                          <LanguageRoute>
                            <Invoices />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/invoices/new"
                        element={
                          <LanguageRoute>
                            <RequireFeature feature="invoicing">
                              <InvoiceCreate />
                            </RequireFeature>
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/invoices/:id"
                        element={
                          <LanguageRoute>
                            <InvoiceDetail />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/invoices/:id/edit"
                        element={
                          <LanguageRoute>
                            <InvoiceEdit />
                          </LanguageRoute>
                        }
                      />
                      {/* Phone capture, and the installed app's start page (/capture gets the language prefix). */}
                      <Route
                        path="/:lang/capture"
                        element={
                          <LanguageRoute>
                            <Capture />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/receipts"
                        element={
                          <LanguageRoute>
                            <Receipts />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/missing-documents"
                        element={
                          <LanguageRoute>
                            <MissingDocuments />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/transactions"
                        element={
                          <LanguageRoute>
                            <Transactions />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/transactions/import-export"
                        element={
                          <LanguageRoute>
                            <TransactionImportExport />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/webfaktura/:id"
                        element={
                          <LanguageRoute>
                            <Webfaktura />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/templates"
                        element={
                          <LanguageRoute>
                            <Templates />
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/templates/new"
                        element={
                          <LanguageRoute>
                            <RequireFeature feature="invoicing">
                              <TemplateCreate />
                            </RequireFeature>
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/templates/:id/edit"
                        element={
                          <LanguageRoute>
                            <TemplateEdit />
                          </LanguageRoute>
                        }
                      />

                      {/* Multi-client overview for accountants */}
                      <Route
                        path="/:lang/business/clients"
                        element={
                          <LanguageRoute>
                            <AccountantClients />
                          </LanguageRoute>
                        }
                      />

                      {/* Business Budget Route */}
                      <Route
                        path="/:lang/business/budget"
                        element={
                          <LanguageRoute>
                            <RequireFeature feature="budgetAnalytics">
                              <BusinessBudgetTracker />
                            </RequireFeature>
                          </LanguageRoute>
                        }
                      />

                      {/* Personal Finances Routes */}
                      <Route
                        path="/:lang/personal/overview"
                        element={
                          <LanguageRoute>
                            <RequireFeature feature="privateMode">
                              <PersonalOverview />
                            </RequireFeature>
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/personal/analytics"
                        element={
                          <LanguageRoute>
                            <RequireFeature feature="privateMode">
                              <PersonalAnalytics />
                            </RequireFeature>
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/personal/budget"
                        element={
                          <LanguageRoute>
                            <RequireFeature feature="privateMode">
                              <BudgetTracker />
                            </RequireFeature>
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/personal/budget/analytics"
                        element={
                          <LanguageRoute>
                            <RequireFeature feature="privateMode">
                              <BudgetTrackerAnalytics />
                            </RequireFeature>
                          </LanguageRoute>
                        }
                      />
                      <Route
                        path="/:lang/personal/budget-old"
                        element={
                          <LanguageRoute>
                            <RequireFeature feature="privateMode">
                              <Budget />
                            </RequireFeature>
                          </LanguageRoute>
                        }
                      />

                      <Route
                        path="/:lang/icons"
                        element={
                          <LanguageRoute>
                            <IconDownloads />
                          </LanguageRoute>
                        }
                      />

                      {/* All /:lang/admin/* UI: single guard so pages & hooks never run for others */}
                      <Route
                        path="/:lang/admin"
                        element={
                          <AdminRoute>
                            <Outlet />
                          </AdminRoute>
                        }
                      >
                        <Route index element={<AdminOverview />} />
                        <Route path="companies" element={<AdminCompanies />} />
                        <Route path="companies/:id" element={<AdminCompanyDetail />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="users/:id" element={<AdminUserDetail />} />
                        <Route path="inbox" element={<AdminInbox />} />
                        <Route path="billing" element={<AdminBilling />} />
                        <Route path="dev" element={<AdminDev />} />
                        <Route path="growth" element={<AdminGrowth />} />
                        <Route path="banks" element={<BankConnectionsMonitor />} />
                        <Route path="monitoring" element={<MonitoringIncidents />} />
                        <Route path="logs" element={<SyncErrorLogs />} />
                        <Route path="free-access" element={<FreeAccess />} />
                      </Route>
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </DemoBoundary>
                </BrowserRouter>
              </TooltipProvider>
            </OrganizationProvider>
          </ModeProvider>
        </LanguageProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
